import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { RebelColors, RebelContainer, RebelContentWrapper, RebelClientCard, RebelAccentButton, RebelSecondaryButton } from "../../../styles/rebel-experiences";
import { GetDB, generateGUID } from "../../../utils/database/firebase-budget-tool";
import { calculateDateBasedAccommodation, hasDatePricing } from "../../../utils/date-pricing-utils";
import { listenToCollection, listenToDocument, setDocument, updateDocumentProperty } from "../../../utils/firestore-functions";
import { getVoterCookie, updateVoterCookie } from "../../../utils/ranking-cookies";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import RebelBudgetConfig from "./rebel-budget-config";
import RebelBudgetSummary from "./rebel-budget-summary";
import RebelBudgetPropertyTab from "./rebel-budget-property-tab";
import RebelBudgetAddModal from "./rebel-budget-add-modal";
import RebelBudgetSaveModal from "./rebel-budget-save-modal";
import RebelBudgetShareModal from "./rebel-budget-share-modal";
import useExchangeRate from "../../../utils/use-exchange-rates";
import firebase from "firebase/compat/app";

const RebelBudget = ({ collapsed, viewOnly }) => {
    const { id } = useParams();
    const budgetDB = GetDB();

    // Trip configuration state
    const [config, setConfig] = useState({
        companyName: "",
        startDate: "",
        endDate: "",
        attendees: 8,
        currency: "USD$",
    });

    // Properties state
    const [properties, setProperties] = useState([]); // array of villa IDs
    const [villaList, setVillaList] = useState([]); // all available villas
    const [villaData, setVillaData] = useState({}); // villaId -> full villa data
    const [categories, setCategories] = useState([]); // agenda-block-types

    // Tab state: -1 = how to, 0 = summary, 1+ = property tabs
    const [tabKey, setTabKey] = useState(-1);
    const initialLoadDoneRef = useRef(false);

    // Ranking state
    const [selectedRankings, setSelectedRankings] = useState({});
    const [savedRankings, setSavedRankings] = useState({});
    const [allRankings, setAllRankings] = useState({});
    const [voterId, setVoterId] = useState(null);

    // Save state
    const [budgetId, setBudgetId] = useState(id || null);
    const [savedEmail, setSavedEmail] = useState("");
    const [savedContactName, setSavedContactName] = useState("");
    const [showAddModal, setShowAddModal] = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showSaveConfirm, setShowSaveConfirm] = useState(false);
    const [isLoaded, setIsLoaded] = useState(!id); // if no id, ready immediately
    const [isSaving, setIsSaving] = useState(false);

    // Calculate trip length from dates
    const tripLength = (() => {
        if (!config.startDate || !config.endDate) return 0;
        const start = new Date(config.startDate);
        const end = new Date(config.endDate);
        const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        return diff > 0 ? diff : 0;
    })();

    // Load all available villas
    useEffect(() => {
        const unsubscribe = listenToCollection(budgetDB, "rebelVillas", (villas) => {
            setVillaList(villas);
        });
        return unsubscribe;
    }, []);

    // Load agenda-block-types for category names
    useEffect(() => {
        const unsubscribe = listenToCollection(budgetDB, "agenda-block-types", (cats) => {
            setCategories(cats);
        });
        return unsubscribe;
    }, []);

    // Load saved session if id is provided
    useEffect(() => {
        if (!id) return;

        const unsubscribe = listenToDocument(budgetDB, "rebelClientBudgets", id, (data) => {
            if (data) {
                setConfig({
                    companyName: data.companyName || "",
                    startDate: data.startDate || "",
                    endDate: data.endDate || "",
                    attendees: data.attendees || 8,
                    currency: data.currency || "USD$",
                });
                setProperties(data.properties || []);
                setAllRankings(data.rankings || {});
                setBudgetId(id);
                setSavedEmail(data.email || "");
                setSavedContactName(data.contactName || "");
                setIsLoaded(true);
                // Default to How To tab — only on first load, not on subsequent saves
                if (!initialLoadDoneRef.current) {
                    initialLoadDoneRef.current = true;
                    setTabKey(-1);
                }

                // Load villa data for each saved property
                if (data.properties && data.properties.length > 0) {
                    data.properties.forEach(villaId => {
                        loadVillaData(villaId);
                    });
                }
            } else {
                setIsLoaded(true);
            }
        });

        return unsubscribe;
    }, [id]);

    // Initialize voter from cookie
    useEffect(() => {
        const cookieId = budgetId || "rebel-budget-new";
        const voterData = getVoterCookie(cookieId);
        setVoterId(voterData.voterId);
        setSelectedRankings(voterData.lastSubmitted || {});
        setSavedRankings(voterData.lastSubmitted || {});
    }, [budgetId]);

    // Load villa data by ID
    const loadVillaData = (villaId) => {
        const villa = villaList.find(v => v.id === villaId || v._docId === villaId);
        if (villa) {
            setVillaData(prev => ({ ...prev, [villaId]: villa }));
        } else {
            // Villa not yet in list, listen for it directly
            listenToDocument(budgetDB, "rebelVillas", villaId, (data) => {
                if (data) {
                    setVillaData(prev => ({ ...prev, [villaId]: data }));
                }
            });
        }
    };

    // When villaList loads, populate villaData for existing properties
    useEffect(() => {
        if (villaList.length > 0 && properties.length > 0) {
            properties.forEach(villaId => {
                if (!villaData[villaId]) {
                    const villa = villaList.find(v => v.id === villaId || v._docId === villaId);
                    if (villa) {
                        setVillaData(prev => ({ ...prev, [villaId]: villa }));
                    }
                }
            });
        }
    }, [villaList, properties]);

    // Add a property
    const handleAddProperty = (villaId) => {
        if (!properties.includes(villaId)) {
            setProperties(prev => [...prev, villaId]);
            loadVillaData(villaId);
            setShowAddModal(false);
            // Switch to the new property's tab
            setTabKey(properties.length + 1);
        }
    };

    // Remove a property
    const handleRemoveProperty = (villaId) => {
        const idx = properties.indexOf(villaId);
        setProperties(prev => prev.filter(id => id !== villaId));
        setVillaData(prev => {
            const next = { ...prev };
            delete next[villaId];
            return next;
        });
        // Remove any rankings for this property
        setSelectedRankings(prev => {
            const next = { ...prev };
            delete next[villaId];
            return next;
        });
        // Switch to summary tab
        setTabKey(0);
    };

    // Handle config change
    const handleConfigChange = (field, value) => {
        setConfig(prev => ({ ...prev, [field]: value }));
    };

    // Handle ranking change
    const handleRankingChange = (propertyId, rank) => {
        setSelectedRankings(prev => {
            const next = { ...prev };
            if (rank === null) {
                delete next[propertyId];
            } else {
                next[propertyId] = rank;
            }
            return next;
        });
    };

    // Quick save for already-saved budgets (#13)
    const handleQuickSave = async () => {
        if (!budgetId) return;

        setIsSaving(true);
        try {
            const now = firebase.firestore.Timestamp.now();
            const updates = {
                companyName: config.companyName,
                startDate: config.startDate,
                endDate: config.endDate,
                attendees: config.attendees,
                currency: config.currency,
                properties: properties,
                updatedAt: now,
            };

            for (const [key, value] of Object.entries(updates)) {
                updateDocumentProperty(budgetDB, "rebelClientBudgets", budgetId, key, value);
            }

            setIsSaving(false);
            setShowSaveConfirm(true);
            setTimeout(() => setShowSaveConfirm(false), 3000);
        } catch (error) {
            console.error("Error saving:", error);
            setIsSaving(false);
        }
    };

    // Get currency symbol from currency string (e.g., "USD$" -> "$")
    const currencySymbol = config.currency.slice(3);

    // Live exchange rate (all backend data is USD; convert at display time)
    const exchangeRate = useExchangeRate(config.currency.slice(0, 3));

    // Check if a villa has booking warnings for the selected dates
    const getVillaWarnings = (villaId) => {
        const villa = villaData[villaId];
        if (!villa || !config.startDate || !config.endDate || !hasDatePricing(villa)) return [];
        try {
            const breakdown = calculateDateBasedAccommodation(config.startDate, config.endDate, villa);
            return breakdown.warnings || [];
        } catch {
            return [];
        }
    };

    return (
        <RebelContainer>
            <RebelContentWrapper>
                <RebelClientCard>
                    <HeaderRow>
                        <HeaderTitle>R.E.B.E.L Property Comparison</HeaderTitle>
                        {config.companyName && <CompanyName>{config.companyName}</CompanyName>}
                    </HeaderRow>

                    <RebelBudgetConfig
                        config={config}
                        onChange={handleConfigChange}
                        tripLength={tripLength}
                        readOnly={viewOnly}
                    />

                    {/* Tab navigation */}
                    <TabRow>
                        {/* How To tab (#17) */}
                        {tabKey !== -1 ? (
                            <TabButton onClick={() => setTabKey(-1)}>
                                <TabTitle>
                                    <FontAwesomeIcon icon={['fas', 'lightbulb']} style={{ marginRight: 6, fontSize: 12 }} />
                                    How To
                                </TabTitle>
                            </TabButton>
                        ) : (
                            <ActiveTabButton>
                                <TabTitle>
                                    <FontAwesomeIcon icon={['fas', 'lightbulb']} style={{ marginRight: 6, fontSize: 12 }} />
                                    How To
                                </TabTitle>
                            </ActiveTabButton>
                        )}

                        {tabKey !== 0 ? (
                            <TabButton onClick={() => setTabKey(0)}>
                                <TabTitle>Summary</TabTitle>
                            </TabButton>
                        ) : (
                            <ActiveTabButton>
                                <TabTitle>Summary</TabTitle>
                            </ActiveTabButton>
                        )}

                        {properties.map((villaId, i) => {
                            const villa = villaData[villaId];
                            const name = villa?.villaName || villa?.name || `Property ${i + 1}`;
                            const location = villa?.location || "";
                            const tabIdx = i + 1;
                            const warnings = getVillaWarnings(villaId);
                            const hasWarning = warnings.length > 0;
                            const hasBlackout = warnings.some(w => w.type === 'blackout');

                            return tabKey !== tabIdx ? (
                                <TabButton key={villaId} onClick={() => setTabKey(tabIdx)} $hasWarning={hasWarning} $hasBlackout={hasBlackout}>
                                    {!viewOnly && <TabCloseButton onClick={(e) => { e.stopPropagation(); handleRemoveProperty(villaId); }}>&times;</TabCloseButton>}
                                    <TabTitle>{name}</TabTitle>
                                    {location && <TabSubtitle>{location}</TabSubtitle>}
                                </TabButton>
                            ) : (
                                <ActiveTabButton key={villaId} $hasWarning={hasWarning} $hasBlackout={hasBlackout}>
                                    {!viewOnly && <TabCloseButton $active onClick={(e) => { e.stopPropagation(); handleRemoveProperty(villaId); }}>&times;</TabCloseButton>}
                                    <TabTitle>{name}</TabTitle>
                                    {location && <TabSubtitle active>{location}</TabSubtitle>}
                                </ActiveTabButton>
                            );
                        })}

                        {!viewOnly && (
                            <TabButton onClick={() => setShowAddModal(true)}>
                                <TabTitle>+</TabTitle>
                            </TabButton>
                        )}
                    </TabRow>

                    {/* Tab content */}
                    <TabContent>
                        {/* How To tab content (#17) */}
                        {tabKey === -1 && (
                            <HowToContainer>
                                {viewOnly ? (
                                    <>
                                        <HowToTitle>
                                            <FontAwesomeIcon icon={['fas', 'lightbulb']} style={{ marginRight: 10 }} />
                                            How to Rank Properties
                                        </HowToTitle>
                                        <HowToSubtitle>Help choose the perfect retreat property by submitting your rankings:</HowToSubtitle>

                                        <HowToSteps>
                                            <HowToStep>
                                                <StepNumber>1</StepNumber>
                                                <StepContent>
                                                    <StepTitle>Browse the Properties</StepTitle>
                                                    <StepDescription>Use the tabs above to explore each shortlisted property. Review the details, photos, and cost breakdown to get a feel for each option.</StepDescription>
                                                </StepContent>
                                            </HowToStep>

                                            <HowToStep>
                                                <StepNumber>2</StepNumber>
                                                <StepContent>
                                                    <StepTitle>Rank Your Preferences</StepTitle>
                                                    <StepDescription>On each property tab, use the ranking dropdown to assign your preference — 1st choice, 2nd choice, and so on. Each rank can only be used once.</StepDescription>
                                                </StepContent>
                                            </HowToStep>

                                            <HowToStep>
                                                <StepNumber>3</StepNumber>
                                                <StepContent>
                                                    <StepTitle>Submit Your Rankings</StepTitle>
                                                    <StepDescription>Once you've ranked all of the properties, hit the "Submit Rankings" button. Your votes will be combined with the rest of the team's to help decide the final pick.</StepDescription>
                                                </StepContent>
                                            </HowToStep>
                                        </HowToSteps>

                                        <HowToFooter>
                                            <RebelAccentButton onClick={() => setTabKey(0)}>
                                                View Properties →
                                            </RebelAccentButton>
                                        </HowToFooter>
                                    </>
                                ) : (
                                    <>
                                        <HowToTitle>
                                            <FontAwesomeIcon icon={['fas', 'lightbulb']} style={{ marginRight: 10 }} />
                                            Welcome to the R.E.B.E.L Budget Comparison Tool
                                        </HowToTitle>
                                        <HowToSubtitle>Here's how to get started:</HowToSubtitle>

                                        <HowToSteps>
                                            <HowToStep>
                                                <StepNumber>1</StepNumber>
                                                <StepContent>
                                                    <StepTitle>Set Your Retreat Details</StepTitle>
                                                    <StepDescription>Input your group name, number of attendees, travel dates, and preferred currency using the fields at the top of the page.</StepDescription>
                                                </StepContent>
                                            </HowToStep>

                                            <HowToStep>
                                                <StepNumber>2</StepNumber>
                                                <StepContent>
                                                    <StepTitle>Add Properties to Compare</StepTitle>
                                                    <StepDescription>Click the "+" tab to select a new property and see the budget estimate based on your retreat details and selections.</StepDescription>
                                                </StepContent>
                                            </HowToStep>

                                            <HowToStep>
                                                <StepNumber>3</StepNumber>
                                                <StepContent>
                                                    <StepTitle>Explore & Compare</StepTitle>
                                                    <StepDescription>Continue adding properties you're interested in. Each property tab shows a detailed cost breakdown including accommodation, activities, meals, and more.</StepDescription>
                                                </StepContent>
                                            </HowToStep>

                                            <HowToStep>
                                                <StepNumber>4</StepNumber>
                                                <StepContent>
                                                    <StepTitle>Review the Summary</StepTitle>
                                                    <StepDescription>Head to the "Summary" tab for a quick side-by-side comparison of all your selected properties and their costs.</StepDescription>
                                                </StepContent>
                                            </HowToStep>

                                            <HowToStep>
                                                <StepNumber>5</StepNumber>
                                                <StepContent>
                                                    <StepTitle>Save Your Progress</StepTitle>
                                                    <StepDescription>Click "Save & Share" at any time to save your progress and receive a personal link to access and edit your budget comparison in the future.</StepDescription>
                                                </StepContent>
                                            </HowToStep>

                                            <HowToStep>
                                                <StepNumber>6</StepNumber>
                                                <StepContent>
                                                    <StepTitle>Share with Your Team</StepTitle>
                                                    <StepDescription>Once saved, use "Share with Team" to send a view-only link to your group so they can review the properties and submit their rankings.</StepDescription>
                                                </StepContent>
                                            </HowToStep>

                                            <HowToStep>
                                                <StepNumber>7</StepNumber>
                                                <StepContent>
                                                    <StepTitle>Collect Rankings & Decide</StepTitle>
                                                    <StepDescription>Team rankings will be collected and displayed on the Summary tab to help you make the final property selection.</StepDescription>
                                                </StepContent>
                                            </HowToStep>
                                        </HowToSteps>

                                        <HowToFooter>
                                            <RebelAccentButton onClick={() => setTabKey(0)}>
                                                Get Started →
                                            </RebelAccentButton>
                                        </HowToFooter>
                                    </>
                                )}
                            </HowToContainer>
                        )}

                        {tabKey === 0 && (
                            <RebelBudgetSummary
                                properties={properties}
                                villaData={villaData}
                                categories={categories}
                                config={config}
                                tripLength={tripLength}
                                currencySymbol={currencySymbol}
                                exchangeRate={exchangeRate}
                                allRankings={allRankings}
                                selectedRankings={selectedRankings}
                                onRankingChange={handleRankingChange}
                                savedRankings={savedRankings}
                                setSavedRankings={setSavedRankings}
                                voterId={voterId}
                                budgetId={budgetId}
                                budgetDB={budgetDB}
                                savedEmail={savedEmail}
                                savedContactName={savedContactName}
                            />
                        )}

                        {properties.map((villaId, i) => {
                            if (tabKey === i + 1) {
                                return (
                                    <RebelBudgetPropertyTab
                                        key={villaId}
                                        villaId={villaId}
                                        villa={villaData[villaId]}
                                        categories={categories}
                                        config={config}
                                        tripLength={tripLength}
                                        currencySymbol={currencySymbol}
                                        exchangeRate={exchangeRate}
                                        onRemove={() => handleRemoveProperty(villaId)}
                                        viewOnly={viewOnly}
                                    />
                                );
                            }
                            return null;
                        })}
                    </TabContent>

                    {/* Action buttons */}
                    {!viewOnly && (
                        <SaveRow>
                            {/* #13: Show "Save" for quick-save when budget already exists */}
                            {budgetId ? (
                                <>
                                    <SaveButton onClick={handleQuickSave} disabled={isSaving}>
                                        <FontAwesomeIcon icon={['fas', 'save']} style={{ marginRight: 6 }} />
                                        {isSaving ? 'Saving...' : 'Save'}
                                    </SaveButton>
                                    <ShareButton onClick={() => setShowShareModal(true)}>
                                        Share with Team
                                    </ShareButton>
                                </>
                            ) : (
                                <RebelAccentButton onClick={() => setShowSaveModal(true)}>
                                    Save & Share
                                </RebelAccentButton>
                            )}
                        </SaveRow>
                    )}

                    {/* Save confirmation toast */}
                    {showSaveConfirm && (
                        <SaveConfirmToast>
                            <FontAwesomeIcon icon={['fas', 'check-circle']} style={{ marginRight: 8 }} />
                            Budget Comparison Saved
                        </SaveConfirmToast>
                    )}

                    {/* Modals */}
                    {!viewOnly && (
                        <>
                            <RebelBudgetAddModal
                                isOpen={showAddModal}
                                toggle={() => setShowAddModal(false)}
                                villaList={villaList}
                                existingProperties={properties}
                                onAdd={handleAddProperty}
                            />

                            <RebelBudgetSaveModal
                                isOpen={showSaveModal}
                                toggle={() => setShowSaveModal(false)}
                                config={config}
                                properties={properties}
                                rankings={allRankings}
                                budgetId={budgetId}
                                setBudgetId={setBudgetId}
                                budgetDB={budgetDB}
                                setSavedEmail={setSavedEmail}
                                setSavedContactName={setSavedContactName}
                            />

                            <RebelBudgetShareModal
                                isOpen={showShareModal}
                                toggle={() => setShowShareModal(false)}
                                config={config}
                                properties={properties}
                                villaData={villaData}
                                categories={categories}
                                tripLength={tripLength}
                                exchangeRate={exchangeRate}
                                budgetId={budgetId}
                                savedEmail={savedEmail}
                                savedContactName={savedContactName}
                                budgetDB={budgetDB}
                            />
                        </>
                    )}
                </RebelClientCard>
            </RebelContentWrapper>
        </RebelContainer>
    );
};

export default RebelBudget;

const HeaderRow = styled.div`
    text-align: center;
    margin-bottom: 1.5rem;
`;

const HeaderTitle = styled.h1`
    color: ${RebelColors.gold};
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
    letter-spacing: 2px;
`;

const CompanyName = styled.p`
    color: ${RebelColors.textMuted};
    font-size: 1.1rem;
    font-style: italic;
    margin-bottom: 0;
`;

const TabRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin-top: 1.5rem;
`;

const TabButton = styled.button`
    position: relative;
    background: ${props => props.$hasBlackout
        ? 'rgba(239, 68, 68, 0.15)'
        : props.$hasWarning
        ? 'rgba(251, 191, 36, 0.15)'
        : RebelColors.surface};
    border: 2px solid ${props => props.$hasBlackout ? '#ef4444' : props.$hasWarning ? '#fbbf24' : RebelColors.border};
    border-radius: 10px 10px 0 0;
    margin: 0 1px;
    padding: 8px 20px;
    min-height: 52px;
    color: ${RebelColors.text};
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    display: inline-flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    vertical-align: bottom;

    &:hover {
        background: ${props => props.$hasBlackout
            ? 'rgba(239, 68, 68, 0.25)'
            : props.$hasWarning
            ? 'rgba(251, 191, 36, 0.25)'
            : RebelColors.surfaceLight};
        color: ${props => props.$hasBlackout ? '#ef4444' : props.$hasWarning ? '#fbbf24' : RebelColors.gold};
        border-color: ${props => props.$hasBlackout ? '#ef4444' : props.$hasWarning ? '#fbbf24' : RebelColors.gold};
    }

    &:focus {
        outline: none;
        border-color: ${props => props.$hasBlackout ? '#ef4444' : props.$hasWarning ? '#fbbf24' : RebelColors.gold};
    }
`;

const ActiveTabButton = styled.button`
    position: relative;
    background: ${props => props.$hasBlackout
        ? 'rgba(239, 68, 68, 0.3)'
        : props.$hasWarning
        ? 'rgba(251, 191, 36, 0.3)'
        : RebelColors.gold};
    color: ${props => props.$hasBlackout ? '#ef4444' : props.$hasWarning ? '#fbbf24' : '#000000'};
    border: 2px solid ${props => props.$hasBlackout ? '#ef4444' : props.$hasWarning ? '#fbbf24' : RebelColors.gold};
    border-radius: 10px 10px 0 0;
    margin: 0 1px -1px 1px;
    padding: 8px 20px;
    min-height: 52px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: inline-flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    vertical-align: bottom;

    &:hover {
        background: ${props => props.$hasBlackout
            ? 'rgba(239, 68, 68, 0.35)'
            : props.$hasWarning
            ? 'rgba(251, 191, 36, 0.35)'
            : RebelColors.gold};
        color: ${props => props.$hasBlackout ? '#ef4444' : props.$hasWarning ? '#fbbf24' : '#000000'};
    }

    &:focus {
        outline: none;
    }
`;

const TabTitle = styled.div`
    line-height: 1.2;
    display: flex;
    align-items: center;
`;

const TabSubtitle = styled.div`
    font-size: 11px;
    font-weight: 400;
    margin-top: 2px;
    opacity: 0.8;
    color: ${props => props.active ? '#000000' : RebelColors.primaryMuted};
    line-height: 1.2;
`;

const TabCloseButton = styled.span`
    position: absolute;
    top: 2px;
    right: 4px;
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
    color: ${props => props.$active ? 'rgba(0, 0, 0, 0.45)' : 'rgba(255, 255, 255, 0.4)'};
    padding: 0 3px;
    border-radius: 3px;

    &:hover {
        color: ${props => props.$active ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)'};
    }
`;

const TabContent = styled.div`
    margin-left: 1px;
    margin-right: 1px;
    padding: 15px;
    border-radius: 0 0 10px 10px;
    border: 2px solid rgba(255, 215, 0, 0.3);
    background-color: rgba(20, 20, 20, 0.7);
`;

const SaveRow = styled.div`
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-top: 1.5rem;
`;

const SaveConfirmToast = styled.div`
    text-align: center;
    margin-top: 1rem;
    padding: 10px 20px;
    background: rgba(34, 197, 94, 0.15);
    border: 1px solid rgba(34, 197, 94, 0.4);
    border-radius: 8px;
    color: #22c55e;
    font-weight: 600;
    font-size: 0.9rem;
    animation: fadeIn 0.3s ease;

    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-5px); }
        to { opacity: 1; transform: translateY(0); }
    }
`;

const SaveButton = styled.button`
    background: linear-gradient(90deg, #fdb506 0%, #f59e0b 100%);
    border: 2px solid #fdb506;
    color: #000000;
    border-radius: 0.5rem;
    padding: 0.5rem 1.5rem;
    font-weight: 600;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;

    &:hover {
        transform: scale(1.02);
        box-shadow: 0 4px 12px rgba(253, 181, 6, 0.4);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
`;

const ShareButton = styled.button`
    background: transparent;
    border: 2px solid ${RebelColors.gold};
    color: ${RebelColors.gold};
    border-radius: 0.5rem;
    padding: 0.5rem 1.5rem;
    font-weight: 600;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        background: rgba(253, 181, 6, 0.1);
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(253, 181, 6, 0.2);
    }
`;

// ===== How To tab styles (#17) =====

const HowToContainer = styled.div`
    padding: 1rem 0.5rem;
`;

const HowToTitle = styled.h2`
    color: ${RebelColors.gold};
    font-size: 1.3rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
`;

const HowToSubtitle = styled.p`
    color: ${RebelColors.textMuted};
    font-size: 1rem;
    margin-bottom: 1.5rem;
`;

const HowToSteps = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const HowToStep = styled.div`
    display: flex;
    gap: 16px;
    align-items: flex-start;
    padding: 16px;
    background: linear-gradient(135deg, rgba(253, 181, 6, 0.05) 0%, rgba(0, 0, 0, 0.3) 100%);
    border: 1px solid rgba(253, 181, 6, 0.15);
    border-radius: 10px;
    transition: border-color 0.2s ease;

    &:hover {
        border-color: rgba(253, 181, 6, 0.35);
    }
`;

const StepNumber = styled.div`
    background: ${RebelColors.gold};
    color: #000;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.9rem;
    flex-shrink: 0;
`;

const StepContent = styled.div`
    flex: 1;
`;

const StepTitle = styled.h4`
    color: ${RebelColors.text};
    font-size: 0.95rem;
    font-weight: 600;
    margin: 0 0 4px 0;
`;

const StepDescription = styled.p`
    color: ${RebelColors.textMuted};
    font-size: 0.85rem;
    margin: 0;
    line-height: 1.4;
`;

const HowToFooter = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 2rem;
`;
